import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useState, useEffect, useRef } from 'react';
import { logger } from '@/lib/logger';
import { PolarEmbedCheckout } from '@polar-sh/checkout/embed';
import { useQueryClient } from '@tanstack/react-query';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface PricingPlan {
  name: string;
  tier: 'starter' | 'lite' | 'growth' | 'scale';
  monthlyPrice: number;
  annualPrice: number; // Total annual price (11 months = 1 month free)
  monthlyPriceId: string;
  annualPriceId: string;
  productId: string;
  features: string[];
  limits: {
    norths: number | null;
    teamMembers: number;
    storage: number | null;
    aiSummaries: number | null;
  };
}

const plans: PricingPlan[] = [
  {
    name: 'North Starter',
    tier: 'starter',
    monthlyPrice: 67,
    annualPrice: 737, // $67 × 11 months = $737/year (save $67)
    monthlyPriceId: '', // Will be set via Polar
    annualPriceId: '', // Will be set via Polar
    productId: '', // Will be set via Polar
    features: [
      'Up to 3 active North Reports',
      '5 CMAs/month',
      '3 Buyer Reports/month',
      'Unlimited AI summaries',
      'Unlimited storage',
      'Unlimited team members',
    ],
    limits: {
      norths: 3,
      teamMembers: 999,
      storage: null, // Unlimited
      aiSummaries: null, // Unlimited
    },
  },
  {
    name: 'North Lite',
    tier: 'lite',
    monthlyPrice: 99,
    annualPrice: 1089, // $99 × 11 months = $1,089/year (save $99)
    monthlyPriceId: '', // Will be set via Polar
    annualPriceId: '', // Will be set via Polar
    productId: '', // Will be set via Polar
    features: [
      'Up to 12 active North Reports',
      '15 CMAs/month',
      '8 Buyer Reports/month',
      'Offer Strategy AI',
      'Unlimited AI summaries',
      'Unlimited storage',
      'Unlimited team members',
    ],
    limits: {
      norths: 12,
      teamMembers: 999,
      storage: null, // Unlimited
      aiSummaries: null, // Unlimited
    },
  },
  {
    name: 'North Growth',
    tier: 'growth',
    monthlyPrice: 149,
    annualPrice: 1639, // $149 × 11 months = $1,639/year (save $149)
    monthlyPriceId: '', // Will be set via Polar
    annualPriceId: '', // Will be set via Polar
    productId: '', // Will be set via Polar
    features: [
      'Up to 25 active North Reports',
      '35 CMAs/month',
      '20 Buyer Reports/month',
      'Offer Strategy AI',
      'Unlimited AI summaries',
      'Unlimited storage',
      'Unlimited team members',
    ],
    limits: {
      norths: 25,
      teamMembers: 999,
      storage: null, // Unlimited
      aiSummaries: null, // Unlimited
    },
  },
  {
    name: 'North Scale',
    tier: 'scale',
    monthlyPrice: 249,
    annualPrice: 2739, // $249 × 11 months = $2,739/year (save $249)
    monthlyPriceId: '', // Will be set via Polar
    annualPriceId: '', // Will be set via Polar
    productId: '', // Will be set via Polar
    features: [
      'Up to 40 active North Reports',
      'Unlimited CMAs',
      'Unlimited Buyer Reports',
      'Offer Strategy AI',
      'Unlimited AI summaries',
      'Unlimited storage',
      'Unlimited team members',
    ],
    limits: {
      norths: 40,
      teamMembers: 999,
      storage: null, // Unlimited
      aiSummaries: null, // Unlimited
    },
  },
];

interface PricingPlansProps {
  currentPlan?: string;
  autoTriggerPlan?: string | null;
  defaultBillingFrequency?: 'monthly' | 'annual';
  hideToggle?: boolean;
  externalBillingFrequency?: 'monthly' | 'annual';
  onBillingFrequencyChange?: (frequency: 'monthly' | 'annual') => void;
  hideFooterText?: boolean;
  subscriptionStatus?: string; // Add subscription status to determine if canceled
  currentBillingFrequency?: 'monthly' | 'annual'; // Current billing frequency from subscription
}

export function PricingPlans({ currentPlan, autoTriggerPlan, defaultBillingFrequency = 'monthly', hideToggle = false, externalBillingFrequency, onBillingFrequencyChange, hideFooterText = false, subscriptionStatus, currentBillingFrequency }: PricingPlansProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { getEffectiveUserId } = useImpersonation();
  const [loading, setLoading] = useState<string | null>(null);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [selectedPlanForCheckout, setSelectedPlanForCheckout] = useState<PricingPlan | null>(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const checkoutRef = useRef<any>(null);
  const [autoTriggered, setAutoTriggered] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [pendingUpgrade, setPendingUpgrade] = useState<PricingPlan | null>(null);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradePreview, setUpgradePreview] = useState<{
    proratedAmount: number;
    periodEnd: string;
  } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downgradeDialogOpen, setDowngradeDialogOpen] = useState(false);
  const [pendingDowngrade, setPendingDowngrade] = useState<PricingPlan | null>(null);
  const [downgrading, setDowngrading] = useState(false);
  const [internalBillingFrequency, setInternalBillingFrequency] = useState<'monthly' | 'annual'>(defaultBillingFrequency);

  // Use external billing frequency if provided, otherwise use internal state
  const billingFrequency = externalBillingFrequency ?? internalBillingFrequency;
  const setBillingFrequency = onBillingFrequencyChange ?? setInternalBillingFrequency;

  // PolarEmbedCheckout is now imported from npm package, no need to load script

  // Initialize embedded checkout when checkoutUrl is set and user has confirmed
  useEffect(() => {
    if (!checkoutUrl || !checkoutModalOpen) return; // Only run when checkoutModalOpen is true (user confirmed)

    const initializeCheckout = async () => {
      try {
        const theme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
        logger.debug('Creating Polar checkout with URL:', checkoutUrl, 'theme:', theme);
        
        // PolarEmbedCheckout.create() creates its own modal overlay automatically
        const checkout = await PolarEmbedCheckout.create(checkoutUrl, theme);
        checkoutRef.current = checkout;

        logger.debug('Polar checkout created successfully');

        // Listen for checkout events
        checkout.addEventListener('close', () => {
          logger.debug('Checkout closed');
          setCheckoutModalOpen(false);
          setCheckoutUrl(null);
          checkoutRef.current = null;
        });

        // Listen for successful checkout
        checkout.addEventListener('success', async (event: any) => {
          logger.debug('Checkout successful', event.detail);
          
          // Immediately sync subscription from Polar to ensure it's created in database
          // This handles cases where webhook is delayed or fails
          try {
            logger.debug('Syncing subscription from Polar after checkout success...');
            const effectiveUserId = await getEffectiveUserId();
            await supabase.functions.invoke('sync-polar-subscription', {
              body: { effectiveUserId },
            });
            logger.debug('Sync from Polar completed after checkout');
          } catch (syncError) {
            logger.error('Error syncing from Polar after checkout:', syncError);
            // Continue anyway - webhook should handle it, or user can sync manually
          }
          
          // Invalidate query cache to force fresh data fetch on dashboard
          queryClient.invalidateQueries({ queryKey: ['team-usage'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-paginated'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-redirect-check'] });
          
          // Redirect to dashboard on success (unless prevented)
          // Using window.location.href causes full page reload, which will reset query cache
          // but we invalidate first to ensure fresh data on next load
          if (event.detail?.redirect !== false) {
            setTimeout(() => {
              window.location.href = '/dashboard?checkout=success';
            }, 1500); // Give sync a moment to complete
          }
        });

        // Listen for confirmed (payment processing)
        checkout.addEventListener('confirmed', () => {
          logger.debug('Order confirmed, processing payment');
        });

        // Listen for loaded event
        checkout.addEventListener('loaded', () => {
          logger.debug('Checkout loaded');
        });
      } catch (error) {
        logger.error('Failed to initialize Polar checkout:', error);
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to open checkout. Please try again.',
          variant: 'destructive',
        });
        setCheckoutModalOpen(false);
        setCheckoutUrl(null);
        setSelectedPlanForCheckout(null);
      }
    };

    initializeCheckout();

    // Cleanup on unmount or when checkoutUrl changes
    return () => {
      if (checkoutRef.current) {
        try {
          checkoutRef.current.close?.();
        } catch (e) {
          // Ignore cleanup errors
        }
        checkoutRef.current = null;
      }
    };
  }, [checkoutUrl, checkoutModalOpen, toast]);

  // Auto-trigger checkout for pre-selected plan
  useEffect(() => {
    if (autoTriggerPlan && !autoTriggered && !loading) {
      const plan = plans.find(p => p.tier === autoTriggerPlan);
      if (plan) {
        setAutoTriggered(true);
        // Small delay to ensure component is fully mounted
        setTimeout(() => {
          handleUpgrade(plan);
        }, 100);
      }
    }
  }, [autoTriggerPlan, autoTriggered, loading]);


  const handleConfirmUpgrade = async () => {
    if (!pendingUpgrade) return;

    setUpgrading(true);
    try {
      // Check if this is a direct upgrade (active subscription) or needs checkout
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .single();
      
      if (!profile?.team_id) throw new Error('Team not found');
      
      const { data: subscriptionData } = await supabase
        .from('team_subscriptions')
        .select('status')
        .eq('team_id', profile.team_id)
        .maybeSingle();
      
      const subscriptionStatus = subscriptionData?.status;
      
      // If subscription is active, use update-subscription directly
      if (subscriptionStatus === 'active') {
        const { data, error } = await supabase.functions.invoke('update-subscription', {
          body: {
            newPlanTier: pendingUpgrade.tier,
            billingFrequency: billingFrequency,
          },
        });

        if (error) throw error;

        // Close the upgrade dialog
        setUpgradeDialogOpen(false);
        setPendingUpgrade(null);
        setUpgradePreview(null);

        // Sync from Polar to ensure database is updated with latest subscription data
        try {
          logger.debug('Syncing from Polar after upgrade...');
          const syncResult = await supabase.functions.invoke('sync-polar-subscription');
          if (syncResult.error) {
            logger.error('Error syncing after upgrade:', syncResult.error);
          } else {
            logger.debug('Sync from Polar completed successfully');
          }
          // Wait longer for DB to update and propagate
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Verify the sync worked by checking the database
          const { data: verifyData } = await supabase
            .from('team_subscriptions')
            .select('plan_tier, billing_frequency')
            .eq('team_id', profile.team_id)
            .maybeSingle();
          
          logger.debug('Subscription after sync:', {
            plan_tier: verifyData?.plan_tier,
            billing_frequency: verifyData?.billing_frequency,
            expected_tier: pendingUpgrade.tier,
            expected_frequency: billingFrequency
          });
        } catch (syncError) {
          logger.error('Error syncing after upgrade:', syncError);
        }

        toast({
          title: 'Plan Upgraded!',
          description: data.message || `Your plan has been upgraded to ${pendingUpgrade.name}. The page will refresh to show your updated subscription.`,
        });
        
        // Force a full page reload to ensure all data is fresh
        // This ensures the billing page shows the correct plan and billing frequency
        // Use window.location.href instead of reload() to force a full navigation
        setTimeout(() => {
          window.location.href = '/settings?tab=billing';
        }, 2500);
        return;
      }

      // For other statuses (or if update fails), use checkout
      const { data, error } = await supabase.functions.invoke('create-polar-checkout', {
        body: {
          planTier: pendingUpgrade.tier,
          billingFrequency: billingFrequency,
          successUrl: `${window.location.origin}/dashboard?checkout=success`,
          cancelUrl: `${window.location.origin}/select-plan`,
        },
      });

      if (error) throw error;

        if (data?.checkoutUrl) {
          // Close the upgrade dialog and open checkout modal
          setUpgradeDialogOpen(false);
          setPendingUpgrade(null);
          setUpgradePreview(null);

          // Open checkout in embedded modal
          setCheckoutUrl(data.checkoutUrl);
          setCheckoutModalOpen(true);
        } else {
          throw new Error('No checkout URL received');
        }
    } catch (error) {
      logger.error('Upgrade error:', error);

      // Parse error message for user-friendly display
      let errorMessage = 'Failed to start upgrade process';
      if (error instanceof Error) {
        const msg = error.message.toLowerCase();
        if (msg.includes('too small')) {
          errorMessage = 'The prorated amount is too small for checkout. Your upgrade will be processed automatically.';
          // Fall back to direct subscription update
          try {
            const { data: directData, error: directError } = await supabase.functions.invoke('update-subscription', {
              body: {
                newPlanTier: pendingUpgrade.tier,
                billingFrequency: billingFrequency,
              },
            });
            if (directError) throw directError;
            toast({
              title: 'Plan Upgraded!',
              description: directData.message || `Your plan has been upgraded to ${pendingUpgrade.name}.`,
            });
            setUpgradeDialogOpen(false);
            setPendingUpgrade(null);
            setUpgradePreview(null);
            setTimeout(() => window.location.reload(), 1500);
            return;
          } catch (directErr) {
            errorMessage = directErr instanceof Error ? directErr.message : 'Failed to upgrade plan';
          }
        } else {
          errorMessage = error.message;
        }
      }

      toast({
        title: 'Upgrade Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setUpgrading(false);
    }
  };

  const handleConfirmDowngrade = async () => {
    if (!pendingDowngrade) return;

    setDowngrading(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-subscription', {
        body: {
          newPlanTier: pendingDowngrade.tier,
          billingFrequency: billingFrequency,
        },
      });

      if (error) throw error;

      toast({
        title: 'Downgrade Scheduled',
        description: data.message || `Your plan will be downgraded to ${pendingDowngrade.name} at the end of your current billing period.`,
      });

      setDowngradeDialogOpen(false);
      setPendingDowngrade(null);

      // Refresh the page to show updated plan
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      logger.error('Downgrade error:', error);
      toast({
        title: 'Downgrade Failed',
        description: error instanceof Error ? error.message : 'Failed to schedule downgrade',
        variant: 'destructive',
      });
    } finally {
      setDowngrading(false);
    }
  };

  const handleUpgrade = async (plan: PricingPlan) => {
    if (plan.tier === currentPlan) {
      return; // Already on this plan
    }

    setLoading(plan.tier);
    try {
      // First check subscription status to determine if we should use checkout or update
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .single();
      
      if (!profile?.team_id) throw new Error('Team not found');
      
      const { data: subscriptionData } = await supabase
        .from('team_subscriptions')
        .select('status')
        .eq('team_id', profile.team_id)
        .maybeSingle();
      
      const subscriptionStatus = subscriptionData?.status;
      logger.debug('Current subscription status:', subscriptionStatus);
      
      // If subscription is canceled, always use checkout to resubscribe
      if (subscriptionStatus === 'canceled') {
        logger.debug('Subscription is canceled, creating checkout for resubscription');
        const { data, error } = await supabase.functions.invoke('create-polar-checkout', {
          body: {
            planTier: plan.tier,
            billingFrequency: billingFrequency,
            successUrl: `${window.location.origin}/dashboard?checkout=success`,
            cancelUrl: `${window.location.origin}/select-plan`,
          },
        });

        if (error) throw error;

        if (data?.checkoutUrl) {
          setSelectedPlanForCheckout(plan);
          setCheckoutUrl(data.checkoutUrl);
          setShowConfirmationDialog(true);
          setLoading(null);
          return;
        } else {
          throw new Error('No checkout URL received');
        }
      }
      
      // Check if user has an existing subscription (works for both active and trialing subscriptions)
      // If so, update the subscription instead of creating a new checkout
      if (currentPlan) {
        // Determine if this is an upgrade or downgrade
        const tierOrder = { starter: 0, lite: 1, growth: 2, scale: 3 };
        const currentTierOrder = tierOrder[currentPlan as keyof typeof tierOrder] || 0;
        const newTierOrder = tierOrder[plan.tier];
        const isUpgradeAction = newTierOrder > currentTierOrder;

        if (isUpgradeAction) {
          // For upgrades: Check if subscription is trialing first
          // Polar doesn't allow PATCH updates to trialing subscriptions, so we use checkout instead
          // For active subscriptions, show confirmation dialog first
          try {
            // If trialing, upgrades are not allowed during trial period
            // User must wait until trial ends to upgrade
            if (subscriptionStatus === 'trialing') {
              toast({
                title: 'Upgrade Not Available During Trial',
                description: 'Upgrading will be available after your trial period ends. You can continue using your current plan until then.',
                variant: 'default',
              });
              setLoading(null);
              return;
            }
            
            // For active subscriptions, show upgrade confirmation dialog
            setPendingUpgrade(plan);
            setUpgradeDialogOpen(true);
            setLoading(null);
            return;
          } catch (upgradeError) {
            logger.error('Upgrade error:', upgradeError);
            
            // If subscription is trialing, Polar doesn't allow direct updates
            // Fall back to checkout session for upgrade
            const errorMessage = upgradeError instanceof Error ? upgradeError.message : String(upgradeError);
            if (errorMessage.includes('TRIALING_SUBSCRIPTION') || errorMessage.includes('trial period')) {
              logger.debug('Subscription is trialing, using checkout for upgrade instead');
              
              // Use checkout for trialing subscription upgrades
              try {
                const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('create-polar-checkout', {
                  body: {
                    planTier: plan.tier,
                    billingFrequency: billingFrequency,
                    successUrl: `${window.location.origin}/dashboard?checkout=success`,
                    cancelUrl: `${window.location.origin}/select-plan`,
                  },
                });

                if (checkoutError) throw checkoutError;

                if (checkoutData?.checkoutUrl) {
                  // Store the selected plan and checkout URL
                  setSelectedPlanForCheckout(plan);
                  setCheckoutUrl(checkoutData.checkoutUrl);
                  // Show confirmation dialog first
                  setShowConfirmationDialog(true);
                  setLoading(null);
                  return;
                } else {
                  throw new Error('No checkout URL received');
                }
              } catch (checkoutErr) {
                logger.error('Checkout error for trialing upgrade:', checkoutErr);
                throw checkoutErr;
              }
            }
            
            throw upgradeError;
          }
        }

        // Downgrade - check if trialing first
        // Similar to upgrades, we should prevent downgrades during trial
        if (subscriptionStatus === 'trialing') {
          toast({
            title: 'Downgrade Not Available During Trial',
            description: 'Downgrading will be available after your trial period ends. You can continue using your current plan until then.',
            variant: 'default',
          });
          setLoading(null);
          return;
        }
        
        // Downgrade - show confirmation dialog
        setPendingDowngrade(plan);
        setDowngradeDialogOpen(true);
        setLoading(null);
        return;
      }

      // User has no current plan - create new checkout session
      {
        // Create new checkout session with 14-day trial
        const { data, error } = await supabase.functions.invoke('create-polar-checkout', {
          body: {
            planTier: plan.tier,
            billingFrequency: billingFrequency,
            successUrl: `${window.location.origin}/dashboard?checkout=success`,
            cancelUrl: `${window.location.origin}/select-plan`,
          },
        });

        if (error) throw error;

        if (data?.checkoutUrl) {
          // Store the selected plan and checkout URL for confirmation dialog
          setSelectedPlanForCheckout(plan);
          setCheckoutUrl(data.checkoutUrl);
          // Show confirmation dialog first (don't open Polar checkout yet)
          setShowConfirmationDialog(true);
        } else {
          throw new Error('No checkout URL received');
        }
      }
    } catch (error) {
      logger.error('Checkout error:', error);
      toast({
        title: 'Plan Change Failed',
        description: error instanceof Error ? error.message : 'Failed to change plan',
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  // All plans are paid now (no free plan)
  const paidPlans = plans;

  const renderPlanCard = (plan: PricingPlan) => {
    // If subscription is canceled, don't show any plan as "current"
    const isCurrentPlan = subscriptionStatus !== 'canceled' && plan.tier === currentPlan;
    // Growth is most popular, but don't show it if user is already on Growth or Scale
    const isPopular = plan.tier === 'growth' && currentPlan !== 'growth' && currentPlan !== 'scale';

    // Determine if this is an upgrade or downgrade based on tier order
    const tierOrder = { starter: 0, lite: 1, growth: 2, scale: 3 };
    const currentTierOrder = tierOrder[currentPlan as keyof typeof tierOrder] || 0;
    const planTierOrder = tierOrder[plan.tier];
    const isUpgrade = planTierOrder > currentTierOrder;
    const isDowngrade = planTierOrder < currentTierOrder;

    const getButtonLabel = () => {
      if (loading === plan.tier) return 'Processing...';
      
      // Check if same plan but different billing frequency
      const isSamePlan = plan.tier === currentPlan;
      const isDifferentBillingFrequency = currentBillingFrequency && billingFrequency !== currentBillingFrequency;
      
      if (isSamePlan && isDifferentBillingFrequency) {
        return `Switch to ${billingFrequency === 'annual' ? 'Annual' : 'Monthly'}`;
      }
      
      if (isCurrentPlan && subscriptionStatus !== 'canceled') return 'Current Plan';
      // If subscription is canceled, show "Resubscribe" for any plan
      if (subscriptionStatus === 'canceled') return 'Resubscribe';
      // If no current plan, show "Select [Plan Name]"
      if (!currentPlan) return `Select ${plan.name}`;
      if (isUpgrade) return 'Upgrade Now';
      if (isDowngrade) return 'Downgrade';
      return 'Select Plan';
    };

    // Calculate display price based on billing frequency
    const displayPrice = billingFrequency === 'annual'
      ? Math.round(plan.annualPrice / 12) // Show monthly equivalent for annual
      : plan.monthlyPrice;

    const savings = plan.monthlyPrice; // 1 month free = save the monthly price

    return (
      <Card
        key={plan.tier}
        className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg flex flex-col h-full ${
          isCurrentPlan ? 'border-primary ring-2 ring-primary/20' : ''
        } ${isPopular ? 'border-accent shadow-xl' : ''}`}
      >
        {isPopular && (
          <div className="absolute top-0 right-0 bg-gradient-to-l from-accent to-accent/80 text-accent-foreground text-xs font-bold px-4 py-1 rounded-bl-lg">
            MOST POPULAR
          </div>
        )}
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between mb-2">
            <CardTitle className="text-2xl">{plan.name}</CardTitle>
            {isCurrentPlan && (
              <Badge variant="default" className="bg-green-600">Current</Badge>
            )}
          </div>
          <div className="flex flex-col">
            <div className="flex items-baseline gap-1 text-sm text-muted-foreground">
              <span className="text-4xl font-bold text-foreground">
                ${displayPrice}
              </span>
              <span className="text-muted-foreground">/month</span>
            </div>
            {plan.tier === 'starter' && (
              <p className="text-[10px] text-muted-foreground mt-0.5">Six Seeeven 🤷🏼‍♂️</p>
            )}
          </div>
          {billingFrequency === 'annual' && (
            <div className="mt-2 space-y-1">
              <p className="text-sm text-muted-foreground">
                ${plan.annualPrice}/year billed annually
              </p>
              <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                Save ${savings}/year
              </Badge>
            </div>
          )}
        </CardHeader>
        <CardContent className="pb-6 flex-1">
          <ul className="space-y-3">
            {plan.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="rounded-full bg-primary/10 p-0.5 mt-0.5">
                  <Check className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm text-foreground/90">{feature}</span>
              </li>
            ))}
          </ul>
        </CardContent>
        <CardFooter>
          <Button
            className="w-full shadow-lg hover:shadow-xl transition-all"
            variant={isCurrentPlan ? 'outline' : isDowngrade ? 'secondary' : 'default'}
            disabled={
              loading === plan.tier || 
              (isCurrentPlan && subscriptionStatus !== 'canceled' && billingFrequency === currentBillingFrequency)
            }
            onClick={() => handleUpgrade(plan)}
            size="lg"
          >
            {getButtonLabel()}
          </Button>
        </CardFooter>
      </Card>
    );
  };

  return (
    <>
      {/* PolarEmbedCheckout.create() creates its own modal overlay, no need for Dialog wrapper */}
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Billing Frequency Toggle - only show if not hidden */}
        {!hideToggle && (
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative bg-primary/10 p-1 rounded-full inline-flex border border-primary/30">
              <button
                onClick={() => setBillingFrequency('monthly')}
                className={`relative px-6 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                  billingFrequency === 'monthly'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-primary/70 hover:text-primary'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingFrequency('annual')}
                className={`relative px-6 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                  billingFrequency === 'annual'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-primary/70 hover:text-primary'
                }`}
              >
                Annual
              </button>
            </div>
            {billingFrequency === 'annual' && (
              <div className="flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <Badge className="bg-green-600 hover:bg-green-600 text-white px-3 py-1">
                  1 Month Free
                </Badge>
                <span className="text-sm text-muted-foreground">Save up to $249/year</span>
              </div>
            )}
          </div>
        )}

        {/* All Plans - 4 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {paidPlans.map(plan => renderPlanCard(plan))}
        </div>

        {/* Features comparison note - only show on landing/select plan pages, not billing */}
        {!hideFooterText && (
          <div className="text-center text-sm text-muted-foreground pt-4">
            <p className="text-sm text-muted-foreground">
              Start your 14-day free trial today • Cancel anytime
            </p>
          </div>
        )}
      </div>

      {/* Pre-checkout confirmation dialog showing selected plan */}
      <Dialog open={showConfirmationDialog} onOpenChange={(open) => {
        if (!open) {
          setShowConfirmationDialog(false);
          setCheckoutUrl(null);
          setSelectedPlanForCheckout(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Your Subscription</DialogTitle>
            <DialogDescription>
              {selectedPlanForCheckout && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium">
                    You're subscribing to: <span className="font-semibold text-foreground">{selectedPlanForCheckout.name}</span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {billingFrequency === 'annual' ? (
                      <>${selectedPlanForCheckout.annualPrice}/year (billed annually)</>
                    ) : (
                      <>${selectedPlanForCheckout.monthlyPrice}/month</>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground pt-2 border-t">
                    Includes {selectedPlanForCheckout.limits.norths} active North Reports • 14-day free trial
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowConfirmationDialog(false);
                setCheckoutUrl(null);
                setSelectedPlanForCheckout(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // Close confirmation dialog and open Polar checkout
                setShowConfirmationDialog(false);
                setCheckoutModalOpen(true);
              }}
            >
              Continue to Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upgrade Confirmation Dialog */}
      <AlertDialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm Upgrade
            </AlertDialogTitle>
          </AlertDialogHeader>
          {pendingUpgrade && (
            <div className="space-y-4 text-sm text-muted-foreground">
              <div className="space-y-2">
                <p>
                  You are about to upgrade to <span className="font-semibold text-foreground">{pendingUpgrade.name}</span>
                </p>
                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-3">
                  <p className="font-medium text-blue-800 dark:text-blue-200 mb-2">Upgrade Details:</p>
                  <div className="space-y-1 text-blue-700 dark:text-blue-300">
                    <p>
                      <span className="font-semibold">Plan:</span> {pendingUpgrade.name}
                    </p>
                    <p>
                      <span className="font-semibold">Billing:</span> {billingFrequency === 'annual' 
                        ? `$${pendingUpgrade.annualPrice}/year (billed annually)` 
                        : `$${pendingUpgrade.monthlyPrice}/month`}
                    </p>
                    {billingFrequency === 'annual' && (
                      <p className="text-green-700 dark:text-green-300">
                        <span className="font-semibold">Savings:</span> Save ${pendingUpgrade.monthlyPrice}/year with annual billing
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-lg p-4 space-y-2">
                <p className="font-medium text-amber-800 dark:text-amber-200">Important:</p>
                <ul className="list-disc list-inside space-y-1 text-amber-700 dark:text-amber-300">
                  <li>
                    You will be charged{' '}
                    {loadingPreview ? (
                      <span className="inline-block animate-pulse">calculating...</span>
                    ) : upgradePreview && upgradePreview.proratedAmount !== undefined ? (
                      <span className="font-bold text-amber-900 dark:text-amber-100">
                        ${upgradePreview.proratedAmount.toFixed(2)}
                      </span>
                    ) : (
                      <span className="font-bold text-amber-900 dark:text-amber-100">
                        the prorated difference for the remainder of your billing cycle
                      </span>
                    )}{' '}
                    immediately
                  </li>
                  <li>Your new plan features will be available right away</li>
                  <li>
                    Your next full billing cycle will be{' '}
                    {billingFrequency === 'annual'
                      ? `$${pendingUpgrade.annualPrice}/year`
                      : `$${pendingUpgrade.monthlyPrice}/month`}
                  </li>
                  {billingFrequency === 'annual' && (
                    <li className="text-green-700 dark:text-green-300 font-semibold">
                      You're switching to annual billing - enjoy 1 month free!
                    </li>
                  )}
                </ul>
              </div>
              <p>
                Do you want to proceed with this upgrade?
              </p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setPendingUpgrade(null);
                setUpgradePreview(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmUpgrade}
              disabled={upgrading || loadingPreview}
              className="bg-primary hover:bg-primary/90"
            >
              {upgrading ? 'Processing...' : 'Confirm Upgrade'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Downgrade Confirmation Dialog */}
      <AlertDialog open={downgradeDialogOpen} onOpenChange={setDowngradeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm Downgrade
            </AlertDialogTitle>
          </AlertDialogHeader>
          {pendingDowngrade && (
            <div className="space-y-4 text-sm text-muted-foreground">
              <p>
                You are about to downgrade to{' '}
                <span className="font-semibold text-foreground">{pendingDowngrade.name}</span>{' '}
                {billingFrequency === 'annual' ? (
                  `($${pendingDowngrade.annualPrice}/year billed annually)`
                ) : (
                  `($${pendingDowngrade.monthlyPrice}/month)`
                )}.
              </p>
              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4 space-y-2">
                <p className="font-medium text-blue-800 dark:text-blue-200">Important:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300">
                  <li>Your current plan features will remain active until the end of your billing period</li>
                  <li>The downgrade will take effect at your next billing cycle</li>
                  <li>You will <span className="font-semibold">not</span> receive a refund for the current billing period</li>
                  <li>Your new plan limits will be: {pendingDowngrade.limits.norths === null ? 'Unlimited' : pendingDowngrade.limits.norths} North Report{pendingDowngrade.limits.norths === 1 ? '' : 's'}</li>
                </ul>
              </div>
              <p>
                Do you want to proceed with this downgrade?
              </p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingDowngrade(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDowngrade}
              disabled={downgrading}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
            >
              {downgrading ? 'Processing...' : 'Confirm Downgrade'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
